import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingBag, ArrowLeft, Loader2, Star } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  const fetchProductDetails = async () => {
    try {
      // Fetch all products
      const response = await axios.get(`${API}/products`);
      const allProducts = response.data.products.filter(p => p.isActive);
      
      // Find current product
      const currentProduct = allProducts.find(p => p.id === productId);
      
      if (!currentProduct) {
        toast.error('Produit non trouvé');
        navigate('/produits');
        return;
      }
      
      setProduct(currentProduct);
      
      // Find similar products (same category, excluding current)
      const similar = allProducts
        .filter(p => p.category === currentProduct.category && p.id !== productId)
        .slice(0, 3);
      setSimilarProducts(similar);
      
      // Find recommended products (different category, top rated or random)
      const recommended = allProducts
        .filter(p => p.category !== currentProduct.category && p.id !== productId)
        .sort(() => 0.5 - Math.random())
        .slice(0, 3);
      setRecommendedProducts(recommended);
      
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Erreur lors du chargement du produit');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    if (product.stock < 1) {
      toast.error('Produit en rupture de stock');
      return;
    }
    if (quantity > product.stock) {
      toast.error(`Stock insuffisant. Seulement ${product.stock} disponible(s)`);
      return;
    }
    addToCart(product, quantity);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#C9A96E] animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#5D4037] mb-4">Produit non trouvé</p>
          <Button onClick={() => navigate('/produits')} className="bg-[#C9A96E] hover:bg-[#A67B5B]">
            Retour aux produits
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Hero */}
      <section className="relative py-12 bg-[#2C1810]">
        <div className="max-w-7xl mx-auto px-6">
          <Button 
            onClick={() => navigate('/produits')}
            variant="ghost"
            className="text-[#D4C4B5] hover:text-white mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour aux produits
          </Button>
        </div>
      </section>

      {/* Product Detail */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
              {/* Product Image */}
              <div className="aspect-square bg-[#F5F0EB] rounded-xl overflow-hidden">
                {product.image ? (
                  <img 
                    src={product.image.startsWith('/uploads') ? `${BACKEND_URL}${product.image}` : product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingBag size={80} className="text-[#D4C4B5]" />
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="flex flex-col justify-between">
                <div>
                  {product.brand && (
                    <p className="text-[#A67B5B] uppercase text-sm font-medium mb-2">{product.brand}</p>
                  )}
                  <h1 className="text-4xl font-serif font-bold text-[#2C1810] mb-4">{product.name}</h1>
                  
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-4xl font-bold text-[#C9A96E]">{product.price?.toFixed(2)}€</span>
                    {product.stock <= 5 && product.stock > 0 && (
                      <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                        Stock faible ({product.stock} restants)
                      </Badge>
                    )}
                    {product.stock === 0 && (
                      <Badge className="bg-red-100 text-red-800 border-red-200">
                        Épuisé
                      </Badge>
                    )}
                  </div>

                  <div className="mb-6">
                    <h3 className="font-semibold text-[#2C1810] mb-2">Catégorie</h3>
                    <Badge className="bg-[#F5F0EB] text-[#5D4037]">{product.category}</Badge>
                  </div>

                  <div className="mb-6">
                    <h3 className="font-semibold text-[#2C1810] mb-2">Description</h3>
                    <p className="text-[#5D4037] leading-relaxed">{product.description}</p>
                  </div>
                </div>

                {/* Add to Cart */}
                <div className="border-t border-[#E8DDD4] pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <label className="text-[#5D4037] font-medium">Quantité:</label>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="h-10 w-10"
                      >
                        -
                      </Button>
                      <span className="text-[#2C1810] font-semibold w-12 text-center">{quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                        disabled={product.stock === 0}
                        className="h-10 w-10"
                      >
                        +
                      </Button>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleAddToCart}
                    disabled={product.stock === 0}
                    className="w-full bg-[#C9A96E] hover:bg-[#A67B5B] text-white py-6 text-lg"
                  >
                    <ShoppingBag className="mr-2" size={20} />
                    {product.stock === 0 ? 'Produit épuisé' : 'Ajouter au panier'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Similar Products */}
          {similarProducts.length > 0 && (
            <div className="mt-16">
              <h2 className="text-3xl font-serif font-bold text-[#2C1810] mb-8">Produits similaires</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {similarProducts.map((similar) => (
                  <div 
                    key={similar.id}
                    onClick={() => navigate(`/produits/${similar.id}`)}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer"
                  >
                    <div className="aspect-square bg-[#F5F0EB] relative">
                      {similar.image ? (
                        <img 
                          src={similar.image.startsWith('/uploads') ? `${BACKEND_URL}${similar.image}` : similar.image} 
                          alt={similar.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag size={48} className="text-[#D4C4B5]" />
                        </div>
                      )}
                      {similar.stock <= 5 && similar.stock > 0 && (
                        <div className="absolute top-3 right-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          Stock faible
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <p className="text-xs text-[#A67B5B] uppercase mb-1">{similar.brand}</p>
                      <h3 className="text-lg font-semibold text-[#2C1810] mb-2 line-clamp-2">{similar.name}</h3>
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-[#C9A96E]">{similar.price?.toFixed(2)}€</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Products */}
          {recommendedProducts.length > 0 && (
            <div className="mt-16">
              <h2 className="text-3xl font-serif font-bold text-[#2C1810] mb-8">Produits recommandés</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {recommendedProducts.map((recommended) => (
                  <div 
                    key={recommended.id}
                    onClick={() => navigate(`/produits/${recommended.id}`)}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer"
                  >
                    <div className="aspect-square bg-[#F5F0EB] relative">
                      {recommended.image ? (
                        <img 
                          src={recommended.image.startsWith('/uploads') ? `${BACKEND_URL}${recommended.image}` : recommended.image} 
                          alt={recommended.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag size={48} className="text-[#D4C4B5]" />
                        </div>
                      )}
                      {recommended.stock <= 5 && recommended.stock > 0 && (
                        <div className="absolute top-3 right-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          Stock faible
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <p className="text-xs text-[#A67B5B] uppercase mb-1">{recommended.brand}</p>
                      <h3 className="text-lg font-semibold text-[#2C1810] mb-2 line-clamp-2">{recommended.name}</h3>
                      <p className="text-sm text-[#5D4037] mb-2 line-clamp-1">{recommended.category}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-[#C9A96E]">{recommended.price?.toFixed(2)}€</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
